"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronDown, Search, Star, X } from "lucide-react"
import { BottomSheet } from "./bottom-sheet"
import { SideOverlayDrawer } from "./side-overlay-drawer"
import { Button } from "./button"
import { Slider } from "./slider"
import { cn } from "../../lib/utils"
import { useIsDesktop } from "../../hooks/use-is-desktop"

export type FilterCountOption = {
  value: string
  count?: number
  label?: string
}

export type ExperienceLevelOption = {
  value: string
  label: string
  min_years: number
  max_years: number | null
  count: number
}

export type SalaryRangeInfo = {
  min_salary: number
  max_salary: number
  currency: string
  jobs_with_salary: number
}

export type JobFiltersApiResponse = {
  categories?: FilterCountOption[]
  job_types?: FilterCountOption[]
  experience_levels?: ExperienceLevelOption[]
  education_levels?: FilterCountOption[]
  work_modes?: FilterCountOption[]
  countries?: FilterCountOption[]
  states?: FilterCountOption[]
  cities?: FilterCountOption[]
  companies?: FilterCountOption[]
  industries?: FilterCountOption[]
  salary_ranges?: SalaryRangeInfo | null
  salary_buckets?: FilterCountOption[]
}

export type JobFiltersState = {
  categories: string[]
  jobTypes: string[]
  experienceLevels: string[]
  educationLevels: string[]
  workModes: string[]
  country: string
  state: string
  city: string
  salaryRanges: string[]
  companies: string[]
  industries: string[]
  companyRating: number
}

export interface FiltersBottomSheetProps {
  isOpen: boolean
  onClose: () => void
  filters?: JobFiltersApiResponse
  value?: JobFiltersState
  defaultValue?: Partial<JobFiltersState>
  onChange?: (next: JobFiltersState) => void
  onApply?: (next: JobFiltersState) => void
  onReset?: (next: JobFiltersState) => void
  showEmployerFilters?: boolean
  initialTab?: "jobs" | "employer"
}

const defaultState: JobFiltersState = {
  categories: [],
  jobTypes: [],
  experienceLevels: [],
  educationLevels: [],
  workModes: [],
  country: "",
  state: "",
  city: "",
  salaryRanges: [],
  companies: [],
  industries: [],
  companyRating: 0,
}

type NormalizedOption = {
  value: string
  label: string
  count?: number
}

const toTitleCase = (value: string) => {
  if (!value) return ""
  return value
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

const formatValueLabel = (value: string) => {
  if (value.toLowerCase() === "onsite") return "On-site"
  if (value.toLowerCase() === "full time") return "Full Time"
  return toTitleCase(value)
}

const normalizeOptions = (
  options: FilterCountOption[] | undefined,
  formatter?: (value: string) => string
) =>
  (options ?? []).map((option) => ({
    value: option.value,
    label: option.label ?? (formatter ? formatter(option.value) : toTitleCase(option.value)),
    count: option.count,
  }))

const mergeOptions = (...groups: NormalizedOption[][]) => {
  const map = new Map<string, NormalizedOption>()
  groups.flat().forEach((option) => {
    if (!map.has(option.value)) {
      map.set(option.value, option)
    }
  })
  return Array.from(map.values())
}

export function FiltersBottomSheet({
  isOpen,
  onClose,
  filters,
  value,
  defaultValue,
  onChange,
  onApply,
  onReset,
  showEmployerFilters = false,
  initialTab = "jobs",
}: FiltersBottomSheetProps) {
  const [internalState, setInternalState] = useState<JobFiltersState>({
    ...defaultState,
    ...defaultValue,
  })
  const [activeTab, setActiveTab] = useState<"jobs" | "employer">(initialTab)

  useEffect(() => {
    if (!value && isOpen) {
      setInternalState({ ...defaultState, ...defaultValue })
    }
  }, [value, isOpen, defaultValue])

  const { isMounted, isDesktop } = useIsDesktop()

  useEffect(() => {
    if (!showEmployerFilters) {
      setActiveTab("jobs")
    } else {
      setActiveTab(initialTab)
    }
  }, [showEmployerFilters, initialTab])

  const state = value ?? internalState
  const updateState = (next: JobFiltersState) => {
    if (!value) {
      setInternalState(next)
    }
    onChange?.(next)
  }

  const categories = useMemo(
    () => normalizeOptions(filters?.categories, formatValueLabel),
    [filters?.categories]
  )
  const jobTypes = useMemo(
    () => normalizeOptions(filters?.job_types, formatValueLabel),
    [filters?.job_types]
  )
  const experienceLevels = useMemo(
    () =>
      (filters?.experience_levels ?? []).map((option) => ({
        value: option.value,
        label: option.label,
        count: option.count,
      })),
    [filters?.experience_levels]
  )
  const educationLevels = useMemo(
    () => normalizeOptions(filters?.education_levels, formatValueLabel),
    [filters?.education_levels]
  )
  const workModes = useMemo(
    () => normalizeOptions(filters?.work_modes, formatValueLabel),
    [filters?.work_modes]
  )
  const salaryRanges = useMemo(
    () => normalizeOptions(filters?.salary_buckets, formatValueLabel),
    [filters?.salary_buckets]
  )
  const stateOptions = useMemo(
    () => normalizeOptions(filters?.states, formatValueLabel),
    [filters?.states]
  )
  const cityOptions = useMemo(
    () => normalizeOptions(filters?.cities, formatValueLabel),
    [filters?.cities]
  )
  const countryOptions = useMemo(
    () => normalizeOptions(filters?.countries, (v) => v),
    [filters?.countries]
  )
  const companyOptions = useMemo(
    () => normalizeOptions(filters?.companies, formatValueLabel),
    [filters?.companies]
  )
  const industryOptions = useMemo(
    () => normalizeOptions(filters?.industries, formatValueLabel),
    [filters?.industries]
  )

  const resetState = { ...defaultState, ...defaultValue }

  const handleApply = () => {
    onApply?.(state)
    onClose()
  }

  const handleReset = () => {
    updateState(resetState)
    onReset?.(resetState)
  }

  const showEmployerTab = showEmployerFilters

  const renderFilterContent = (showTitle: boolean) => (
    <>
      <div className="space-y-4 font-plus-jakarta-sans text-left">
        {showTitle && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-[#344054]">Filters</h2>
          </div>
        )}

        {showEmployerTab && (
          <div className="flex gap-3 border-b border-[#E4E7EC] text-sm font-medium">
            <button
              type="button"
              onClick={() => setActiveTab("jobs")}
              className={cn(
                "flex-1 pb-2 text-center",
                activeTab === "jobs"
                  ? "border-b-2 border-[#0D41E1] text-[#0D41E1]"
                  : "text-[#667085]"
              )}
            >
              Jobs
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("employer")}
              className={cn(
                "flex-1 pb-2 text-center",
                activeTab === "employer"
                  ? "border-b-2 border-[#0D41E1] text-[#0D41E1]"
                  : "text-[#667085]"
              )}
            >
              Employer
            </button>
          </div>
        )}

        {(activeTab === "jobs" || !showEmployerTab) && (
          <div className="space-y-4">
            {/* Country / Market */}
            {countryOptions.length > 0 && (
              <>
                <FilterSection title="Country">
                  <FilterChipGroup
                    options={countryOptions}
                    selected={state.country ? [state.country] : []}
                    onChange={(next) =>
                      updateState({
                        ...state,
                        country: next[next.length - 1] ?? "",
                        state: "",
                        city: "",
                      })
                    }
                  />
                </FilterSection>
                <Divider />
              </>
            )}

            {jobTypes.length > 0 && (
              <>
                <FilterSection title="Job Type">
                  <FilterChipGroup
                    options={jobTypes}
                    selected={state.jobTypes}
                    onChange={(next) => updateState({ ...state, jobTypes: next })}
                    showAll
                  />
                </FilterSection>
                <Divider />
              </>
            )}

            {experienceLevels.length > 0 && (
              <>
                <FilterSection title="Experience Level">
                  <FilterChipGroup
                    options={experienceLevels}
                    selected={state.experienceLevels}
                    onChange={(next) => updateState({ ...state, experienceLevels: next })}
                    showAll
                  />
                </FilterSection>
                <Divider />
              </>
            )}

            {educationLevels.length > 0 && (
              <>
                <FilterSection title="Education Level">
                  <FilterChipGroup
                    options={educationLevels}
                    selected={state.educationLevels}
                    onChange={(next) => updateState({ ...state, educationLevels: next })}
                  />
                </FilterSection>
                <Divider />
              </>
            )}

            {workModes.length > 0 && (
              <>
                <FilterSection title="Work Mode">
                  <FilterChipGroup
                    options={workModes}
                    selected={state.workModes}
                    onChange={(next) => updateState({ ...state, workModes: next })}
                    showAll
                  />
                </FilterSection>
                <Divider />
              </>
            )}

            {(stateOptions.length > 0 || cityOptions.length > 0) && (
              <>
                {stateOptions.length > 0 && (
                  <FilterSection title="State">
                    <FilterSelect
                      placeholder="Select state"
                      options={stateOptions}
                      value={state.state}
                      onChange={(next) =>
                        updateState({ ...state, state: next, city: "" })
                      }
                    />
                  </FilterSection>
                )}
                {cityOptions.length > 0 && (
                  <FilterSection title="City">
                    <FilterSelect
                      placeholder="Select city"
                      options={cityOptions}
                      value={state.city}
                      onChange={(next) => updateState({ ...state, city: next })}
                    />
                  </FilterSection>
                )}
                <Divider />
              </>
            )}

            {salaryRanges.length > 0 && (
              <>
                <FilterSection title="Salary Range">
                  <FilterChipGroup
                    options={salaryRanges}
                    selected={state.salaryRanges}
                    onChange={(next) => updateState({ ...state, salaryRanges: next })}
                    showAll
                  />
                </FilterSection>
                <Divider />
              </>
            )}
          </div>
        )}

        {showEmployerTab && activeTab === "employer" && (
          <div className="space-y-4">
            <FilterSection title="Company Rating">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#667085]">
                    Company Rating
                  </span>
                  <span className="flex items-center gap-1 text-base font-semibold text-[#ED5AA9]">
                    {state.companyRating.toFixed(1)}
                    <Star className="h-4 w-4 text-[#ED5AA9]" />
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#98A2B3]">0</span>
                  <Slider
                    min={0}
                    max={5}
                    step={0.5}
                    value={[state.companyRating]}
                    onValueChange={(next) =>
                      updateState({ ...state, companyRating: next[0] })
                    }
                    showRange={false}
                    className="flex-1"
                  />
                  <span className="text-xs text-[#98A2B3]">5</span>
                </div>
              </div>
            </FilterSection>
            <Divider />

            {companyOptions.length > 0 && (
              <>
                <FilterSection title="Company">
                  <FilterSelect
                    placeholder="Select company"
                    options={companyOptions}
                    value={state.companies[0] ?? ""}
                    onChange={(next) =>
                      updateState({
                        ...state,
                        companies: next ? [next] : [],
                      })
                    }
                  />
                </FilterSection>
                <Divider />
              </>
            )}

            {industryOptions.length > 0 && (
              <FilterSection title="Industry">
                <FilterSelect
                  placeholder="Select industry"
                  options={industryOptions}
                  value={state.industries[0] ?? ""}
                  onChange={(next) =>
                    updateState({
                      ...state,
                      industries: next ? [next] : [],
                    })
                  }
                />
              </FilterSection>
            )}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 -mx-3 mt-4 border-t border-[#E4E7EC] bg-[#FFFAEF] px-3 pb-2 pt-3">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 rounded-lg px-3 py-3 text-base font-medium text-[#667085] hover:bg-black/5 transition-colors"
          >
            Reset
          </button>
          <Button
            type="button"
            onClick={handleApply}
            className="flex-1 rounded-lg border border-black bg-[#0D41E1] px-3 py-3 text-base font-medium text-white shadow-[0px_4px_0px_0px_#000000]"
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </>
  )

  if (!isMounted) return null

  if (isDesktop) {
    return (
      <SideOverlayDrawer
        isOpen={isOpen}
        onClose={onClose}
        title="Filters"
        className="bg-[#FFFAEF] text-[#101828]"
      >
        {renderFilterContent(false)}
      </SideOverlayDrawer>
    )
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      className="bg-[#FFFAEF] px-3 pb-0 pt-2 text-[#101828]"
      showDragHandle
      title={null}
    >
      {renderFilterContent(true)}
    </BottomSheet>
  )
}

function Divider() {
  return <div className="h-px w-full bg-[#D4D4D4]" />
}

function FilterSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-[#667085]">{title}</p>
      {children}
    </div>
  )
}

function FilterChipGroup({
  options,
  selected,
  onChange,
  showAll = false,
  allLabel = "All",
}: {
  options: NormalizedOption[]
  selected: string[]
  onChange: (next: string[]) => void
  showAll?: boolean
  allLabel?: string
}) {
  const isAllSelected = selected.length === 0

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {showAll && options.length > 0 && (
        <FilterChip
          label={allLabel}
          selected={isAllSelected}
          onClick={() => onChange([])}
        />
      )}
      {options.map((option) => {
        const label = option.label
        return (
          <FilterChip
            key={option.value}
            label={label}
            selected={selected.includes(option.value)}
            onClick={() => handleToggle(option.value)}
          />
        )
      })}
    </div>
  )
}

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors",
        selected
          ? "border-[#0D41E1] text-[#0D41E1]"
          : "border-black text-[#475467]"
      )}
    >
      <span>{label}</span>
      {selected && <X className="h-3 w-3" />}
    </button>
  )
}

function FilterSelect({
  placeholder,
  options,
  value,
  onChange,
}: {
  placeholder: string
  options: NormalizedOption[]
  value: string
  onChange: (value: string) => void
}) {
  const hasOptions = options.length > 0
  const resolvedValue = value || ""

  return (
    <select
      value={resolvedValue}
      onChange={(event) => onChange(event.target.value)}
      disabled={!hasOptions}
      className={cn(
        "h-11 w-full rounded-lg border border-black bg-white px-3 text-sm text-[#667085] shadow-[0_2px_0_0_#000000]",
        !hasOptions && "opacity-60"
      )}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((option) => {
        const label = option.label
        return (
          <option key={option.value} value={option.value}>
            {label}
          </option>
        )
      })}
    </select>
  )
}
